import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Popover,
  Backdrop,
  Fade,
  Card,
  CardContent,
  CardActions,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Search as SearchIcon,
  TourIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import logger from './../utils/logger.js';

const guidanceSteps = [
  {
    id: 'url-search',
    target: 'input[data-testid="company-research-btn"]', // More specific selector
    title: {
      en: 'Welcome to the URL Search!',
      nl: 'Welkom bij de URL Zoekfunctie!'
    },
    content: {
      en: 'Start your research here! Enter any company website URL in this search bar to instantly research and gather sales intelligence grounded in technical data. Try entering "eshgro.nl" or any company website.',
      nl: 'Start hier je onderzoek! Voer hier een bedrijfswebsite URL in om direct technisch ondersteunde verkoop-intelligence te verzamelen. Probeer "eshgro.nl" of een andere bedrijfswebsite in te voeren.'
    },
    position: 'bottom',
    highlight: true,
    priority: 1
  },
  {
    id: 'company-search',
    target: '.MuiTab-root[role="tab"]', // Target the first tab for data table
    title: {
      en: 'Your Data Table',
      nl: 'Je Gegevenstabel'
    },
    content: {
      en: 'View and manage all your uploaded data and research results here. This is where your company data lives.',
      nl: 'Bekijk en beheer al je geüploade gegevens en onderzoeksresultaten hier. Dit is waar je bedrijfsgegevens worden opgeslagen.'
    },
    position: 'bottom',
    priority: 2
  },
  {
    id: 'menu-features',
    target: '.MuiButton-root[aria-label="menu"]', // Target the menu button
    title: {
      en: 'Explore More Features',
      nl: 'Ontdek Meer Functies'
    },
    content: {
      en: 'Click here to access additional features like company research, sales outreach tools, and more.',
      nl: 'Klik hier om toegang te krijgen tot extra functies zoals bedrijfsonderzoek, verkooptools en meer.'
    },
    position: 'bottom',
    priority: 3
  }
];

const UserGuidance = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [guidanceCompleted, setGuidanceCompleted] = useState(false);
  const timeoutRef = useRef(null);

  // Check if user has seen guidance before
  useEffect(() => {
    const checkGuidanceStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has completed onboarding
        const response = await api.get('/api/user/guidance-status');
        if (response.data.success) {
          const hasCompletedGuidance = response.data.data?.guidance_completed || false;
          setGuidanceCompleted(hasCompletedGuidance);
          
          // Show guidance only for new users who haven't completed it
          if (!hasCompletedGuidance) {
            // Wait for all modals to be dismissed and page to be fully loaded
            timeoutRef.current = setTimeout(() => {
              // Check if any blocking modals are open
              const hasOpenModals = document.querySelector('[role="dialog"]') || 
                                   document.querySelector('.MuiDialog-root') ||
                                   document.querySelector('.MuiModal-root');
              
              if (!hasOpenModals) {
                setIsVisible(true);
                startGuidance();
              } else {
                // Retry after another delay if modals are still open
                setTimeout(() => {
                  setIsVisible(true);
                  startGuidance();
                }, 2000);
              }
            }, 2000); // Reduced delay but added modal checking
          }
        }
      } catch (error) {
        logger.error('Error checking guidance status:', error);
        // On error, assume it's a new user and show guidance with same logic
        timeoutRef.current = setTimeout(() => {
          const hasOpenModals = document.querySelector('[role="dialog"]') || 
                               document.querySelector('.MuiDialog-root') ||
                               document.querySelector('.MuiModal-root');
          
          if (!hasOpenModals) {
            setIsVisible(true);
            startGuidance();
          } else {
            setTimeout(() => {
              setIsVisible(true);
              startGuidance();
            }, 2000);
          }
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    checkGuidanceStatus();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user]);

  const startGuidance = () => {
    const step = guidanceSteps[0];
    
    // More robust element finding with multiple attempts
    const findTargetElement = () => {
      const selectors = [
        step.target,
        'input[data-testid="company-research-btn"]',
        'input[aria-label="search urls"]',
        'input[placeholder*="website"]',
        '.MuiTextField-root input',
        'header input[type="text"]'
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element && element.offsetParent !== null) { // Check if element is visible
          return element;
        }
      }
      return null;
    };
    
    const targetElement = findTargetElement();
    
    logger.log(`Looking for element with selector: ${step.target}`);
    logger.log('Found element:', targetElement ? targetElement.tagName + (targetElement.className ? '.' + targetElement.className : '') : 'null');
    
    if (targetElement) {
      // Ensure element is in view and wait a bit for any animations
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        
        // Wait for scroll to complete before showing popover
        setTimeout(() => {
          setAnchorEl(targetElement);
        }, 500);
      }, 100);
    } else {
      logger.warn('No suitable target element found, retrying...');
      // Retry after a short delay
      setTimeout(() => {
        const retryElement = findTargetElement();
        if (retryElement) {
          retryElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          setTimeout(() => setAnchorEl(retryElement), 500);
        } else {
          // If still no element found, show guidance without anchor
          logger.warn('Target element still not found, showing guidance without anchor');
          setAnchorEl(document.body);
        }
      }, 1000);
    }
  };

  const nextStep = () => {
    if (currentStep < guidanceSteps.length - 1) {
      const nextStepIndex = currentStep + 1;
      const step = guidanceSteps[nextStepIndex];
      const targetElement = document.querySelector(step.target);
      
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setAnchorEl(targetElement);
        setCurrentStep(nextStepIndex);
      } else {
        logger.warn(`Target element not found for step: ${step.target}`);
        // Skip to next step or complete if this was the last
        if (nextStepIndex < guidanceSteps.length - 1) {
          setCurrentStep(nextStepIndex + 1);
        } else {
          completeGuidance();
        }
      }
    } else {
      completeGuidance();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const prevStepIndex = currentStep - 1;
      const step = guidanceSteps[prevStepIndex];
      const targetElement = document.querySelector(step.target);
      
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setAnchorEl(targetElement);
        setCurrentStep(prevStepIndex);
      }
    }
  };

  const closeGuidance = () => {
    setIsVisible(false);
    setAnchorEl(null);
  };

  const completeGuidance = async () => {
    try {
      // Mark guidance as completed for this user
      await api.post('/api/user/complete-guidance');
      setGuidanceCompleted(true);
    } catch (error) {
      logger.error('Error marking guidance as completed:', error);
    }
    closeGuidance();
  };

  const skipGuidance = async () => {
    await completeGuidance();
  };

  if (isLoading || !isVisible || guidanceCompleted) {
    return null;
  }

  const currentStepData = guidanceSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === guidanceSteps.length - 1;

  return (
    <>
      {/* Less intrusive backdrop */}
      <Backdrop
        open={isVisible}
        sx={{
          zIndex: 10000, // Higher z-index to be above other modals
          backgroundColor: 'rgba(0, 0, 0, 0.3)', // Even lighter backdrop
          '& > *': {
            position: 'relative'
          }
        }}
        onClick={(e) => {
          // Allow clicking on backdrop to close if user wants
          e.stopPropagation();
        }}
      />

      {/* Popover with guidance content */}
      <Popover
        open={isVisible && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeGuidance}
        anchorOrigin={{
          vertical: anchorEl === document.body ? 'top' : 'bottom',
          horizontal: anchorEl === document.body ? 'left' : 'center',
        }}
        transformOrigin={{
          vertical: anchorEl === document.body ? 'top' : 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            maxWidth: 420,
            zIndex: 10001, // Higher than backdrop
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '2px solid',
            borderColor: 'primary.main'
          }
        }}
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        sx={{
          zIndex: 10001, // Ensure popover is above everything
          '& .MuiPopover-paper': {
            mt: anchorEl === document.body ? 2 : 1,
            ml: anchorEl === document.body ? 2 : 0,
          }
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isFirstStep && <SearchIcon color="primary" sx={{ mr: 1 }} />}
                <Typography variant="h6" component="h3">
                  {currentStepData.title[language] || currentStepData.title.en}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={closeGuidance}
                sx={{ mt: -1, mr: -1 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentStepData.content[language] || currentStepData.content.en}
            </Typography>

            {/* Progress indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {language === 'nl' ? 'Stap' : 'Step'} {currentStep + 1} {language === 'nl' ? 'van' : 'of'} {guidanceSteps.length}
              </Typography>
              <Box sx={{ flexGrow: 1, mx: 2 }}>
                <Box
                  sx={{
                    height: 4,
                    backgroundColor: 'grey.300',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: 'primary.main',
                      width: `${((currentStep + 1) / guidanceSteps.length) * 100}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>

          <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
            <Box>
              <Button
                size="small"
                onClick={skipGuidance}
                color="inherit"
              >
                {language === 'nl' ? 'Overslaan' : 'Skip Tour'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isFirstStep && (
                <Button
                  size="small"
                  onClick={prevStep}
                  startIcon={<PrevIcon />}
                  variant="outlined"
                >
                  {language === 'nl' ? 'Vorige' : 'Previous'}
                </Button>
              )}
              
              <Button
                size="small"
                onClick={isLastStep ? completeGuidance : nextStep}
                endIcon={isLastStep ? <CheckCircleIcon /> : <NextIcon />}
                variant="contained"
                color="primary"
              >
                {isLastStep 
                  ? (language === 'nl' ? 'Voltooien' : 'Complete')
                  : (language === 'nl' ? 'Volgende' : 'Next')
                }
              </Button>
            </Box>
          </CardActions>
        </Card>
      </Popover>

      {/* Custom styles to highlight the target element */}
      {isVisible && anchorEl && anchorEl !== document.body && (
        <style>
          {`
            ${currentStepData.target} {
              position: relative !important;
              z-index: 9999 !important;
              box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.5), 0 0 20px rgba(25, 118, 210, 0.3) !important;
              border-radius: 8px !important;
              background-color: white !important;
            }
          `}
        </style>
      )}
    </>
  );
};

export default UserGuidance; 